import { VesselPlanningModelBase, VoyagePlan, PlanStatus, VesselCharacteristics, CapturedPlanNotifications, RouteAdoptionResult, RouteAdoptionPortPair } from "./vessel-planning-model";
import { VesselPlansService, PlanWithId } from "../services/vessel-plans-service";
import { VoyageSchedulesModel } from "./voyage-schedules-model";
import { VoyageDialogPopupManager } from '../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { compareRoutes, debounce, deepCopy, deepEqual, deepEqualPlans, findLastIndex, GetFormattedDate, getLatestWeekDate, UUID, isOnline } from "../utils/helpers";
import { AlertType, VesselAlertsModel } from "./vessel-alerts-model";
import { BprAdoption, RouteGeometry, RoutingJobOptions, VoyageRoute, VoyageRouteLineFeature, VoyageRoutePointFeature, VoyageRoutingModel, WeatherRoutingInfo, WniRequiredReason } from "./voyage-routing-model";
import { VesselPositionModel } from "./vessel-position-model";
import { VesselPerformanceModel } from "./vessel-performance-model";
import { WeatherModel } from "./weather-model";
import { getPilotPointIndeces, getRouteDistance, isCrossingWeatherRoutingLines, markPassedAndAddCurrentPosition, verifyRequestedETAs } from "../utils/route-utils";
import { VesselEnpBookAndChapterModel } from '../models/vessel-enp-book-and-chapter-model';
import { VesselNavAreasModel } from '../models/vessel-nav-areas-model';
import { VesselRs1345Model } from "./vessel-rs1345-model";
import { VesselRs6Model } from "./vessel-rs6-model";
import { DataExtractor } from "../../local_modules/voyage-passage-plan/src/data-extractor";
import { PassagePlanEvents, SuggestedRouteEvents } from "../../local_modules/voyage-suggested-routes/src/types";
import { VesselAlarmsModel } from "./vessel-alarms-model";
import { AlarmState, NotificationTypes } from "../services/types/vessel-notifications-services.types";
import { VoyageDialogPopupBodyWarning } from "../../local_modules/voyage-dialog-popup/src/voyage-dialog-popup-body-warning";
import { SvgIcons } from "../../local_modules/voyage-svg-icons/src/voyage-svg-icons";
import { AddWeatherRoutingInfoObject, mergeWrInfoForStatusUpdate, WRStatus } from "../../local_modules/voyage-suggested-routes/src/helper";
import { AlertService } from "../services/alert-service";
import { IndexedDBService } from "../../indexed-db/services/db_service";
import { VoyageImoModel } from "./voyage-imo-model";
import { VoyageImoEventNames } from "./voyage-imo-model-impl";
import { IndexedDraftsDBService } from "../../indexed-db/services/drafts_db_service";
import { ScheduleEntry } from "./types/voyage-schedules-model.types";
import { TerminalsModel } from "./terminals-model";
import { VesselNotificationsService } from "../services/vessel-notifications-service";
import { VesselNotificationsModel } from "./vessel-notifications-model";
import { WeatherRoutingModel } from "./weather-routing-reports-model";
import { ISWEATHERROUTINGENABLED } from "./feature-flag-model-impl";
import { FeatureFlagModel } from "./feature-flag-model";
import { VoyageWeatherRoutingModel } from "./weather-routing-model";
import { faro } from "../utils/rum-utility";

const CheckExecutingPlanInterval = 60 * 1000; // Poll once a minute for changes
const PlansStaleAge = 60 * 1000; // Re-fetch when plans fetch age is older than this (only on demand)
const AlertsPollingInterval = 60000; // Poll every minute

const isVesselOnCloud: boolean = (window as any).appenv?.vesselOnCloud;
interface PlansAndTime {
    fetchedTime: number;
    plans: PlanWithId[];
}

export interface HeavyWeatherViolation {
  parameter: string;
  forecast_value: number | string;
  company_limit_value: number | string;
}

export class VesselPlanningModelImpl extends VesselPlanningModelBase {

    private _WeatherRoutingOn: boolean = false;
    constructor() {
        super();

        this._WeatherRoutingOn = localStorage.getItem(ISWEATHERROUTINGENABLED) === "true";

        this._loadPlansPromise = this._loadPlans();
        this._updateExecutingPlanPromise = this._updateExecutingPlan();
        setInterval(() => this._updateExecutingPlanPromise = this._updateExecutingPlan(), CheckExecutingPlanInterval);

        this._onScheduleChanged = this._onScheduleChanged.bind(this);
        this._onPositionUpdated = this._onPositionUpdated.bind(this);
        this._updateFeatureFlags = this._updateFeatureFlags.bind(this);

        VesselPositionModel!.addEventListener("position-updated", this._onPositionUpdated);
        VoyageSchedulesModel!.addEventListener("schedule-updated", this._onScheduleChanged);
        FeatureFlagModel?.addEventListener('featureFlagsUpdated', this._updateFeatureFlags);

        this._debouncedCheckAndShowRollWarning = debounce(this._checkAndShowRollWarning, 1000, this);
        // reseting _vesselCharacteristics on IMO change
        VoyageImoModel?.addEventListener(VoyageImoEventNames.ImoUpdated, () => this._vesselCharacteristics = null);
        if(!isVesselOnCloud) {
           this._startPollingForAlerts(); // excluding for cloud
        }
    }

    private _updateFeatureFlags() {
        this._WeatherRoutingOn = localStorage.getItem(ISWEATHERROUTINGENABLED) === "true";
    }

    private _startPollingForAlerts() {
        this._pollActiveAlerts(); 
        setInterval(() => this._pollActiveAlerts(), AlertsPollingInterval);
    }

    private async _pollActiveAlerts(): Promise<void> {
        const activeAlerts = await AlertService.getPassedPilotAlert();
        this.showAlertForActiveAlerts(activeAlerts)
    }

    showAlertForActiveAlerts(activeAlerts: string[]): void {
        if (activeAlerts.length > 0) {
            VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, {
                message: "Voyage has been recalculated passing Pilot off Waypoint. Please re-evaluate new power advice.",
                id: AlertType.PlanRecalculatedPilotOff
            });
        } else {
            VesselAlertsModel!.removeAlert((<any>window)["appenv"].vesselIMONumber, AlertType.PlanRecalculatedPilotOff);
        }
    }

    async getPlans(scheduleId: string): Promise<VoyagePlan[]> {
        await this._refreshPlans(scheduleId);
        return this._plans.get(scheduleId)!.plans.map(plan => this._createPlanObject(plan));
    }

    async getAllPlans(): Promise<VoyagePlan[]> {
        await this._loadPlansPromise;
        const allPlans: VoyagePlan[] = [];
        for (const plansInfo of this._plans.values()) {
            allPlans.push(...plansInfo.plans.map(plan => this._createPlanObject(plan)));
        }
        return allPlans;
    }

    async getCurrentPlan(scheduleId: string): Promise<VoyagePlan | null> {
        if (await this.hasPlan(scheduleId)) {
            return this._createPlanObject(this._plans.get(scheduleId)!.plans[0]);
        }
        return null;
    }

    async getApprovedPlan(scheduleId: string): Promise<VoyagePlan | null> {
        await this._refreshPlans(scheduleId);
        const plans = this._plans.get(scheduleId)!.plans;
        for (const plan of plans) {
            if (plan.status === "approved") {
                return this._createPlanObject(plan);
            }
        }
        return null;
    }

    async getAllPlansStatus(): Promise<{ [index: string]: PlanStatus }> {
        await this._loadPlansPromise;
        const statuses: { [index: string]: PlanStatus } = {};

        for (const [scheduleId, plansInfo] of this._plans) {
            if (!statuses[scheduleId] && plansInfo.plans.length > 0) {
                statuses[scheduleId] = plansInfo.plans[0].status;
            }
        }

        return statuses;
    }

    async getExecutingPlan(): Promise<VoyagePlan | null> {
        await this._updateExecutingPlanPromise;
        return this._executingPlan;
    }

    async hasPlan(scheduleId: string): Promise<boolean> {
        await this._refreshPlans(scheduleId);
        return this._plans.get(scheduleId)!.plans.length > 0;
    }

    async createPlan(scheduleId: string): Promise<VoyagePlan> {
        if (await this.hasPlan(scheduleId)) {
            return this._plans.get(scheduleId)!.plans[0]
        }

        const newPlan = this._getBlankPlan(scheduleId)
        await this._createNewPlanEntry(newPlan)

        return this._createPlanObject(newPlan)
    }

    async setPlan(scheduleId: string, plan: VoyagePlan, sendToShore: boolean, isWrManualUpdateRequest = false): Promise<void> {
        await this._refreshPlans(scheduleId);

        const plans = this._plans.get(scheduleId)!.plans;

        if (!plans || !plans.length) {
            return;
        }

        const oldPlan = plans[0]

        if (!this._hasChanges(oldPlan, plan) && !isWrManualUpdateRequest && !sendToShore) {
            // No changes in plan, no need to make put call to update plan
            return;
        }

        // checking if we should update existing plan rather than creating a new one
        const oldPlanApprovedRouteID = oldPlan.routes[plans[0].selectedRouteIndex]?.routeId;

        const newPlanApprovedRouteID = plan.routes[plan.selectedRouteIndex]?.routeId;

        // Force autoUpdateReason to be blank (should only be set by routeoptimiser)
        plan.autoUpdateReason = "";

        if (this._WeatherRoutingOn) {
            for (let route of plan?.routes || []) {
                if (!route?.weatherRoutingInfo) {
                    route = AddWeatherRoutingInfoObject(route);
                }

                // Return if the required reason is manual or heavyweather
                if (route?.weatherRoutingInfo?.required && 
                    (route?.weatherRoutingInfo?.required_reason === WniRequiredReason.Manual || route?.weatherRoutingInfo?.required_reason === WniRequiredReason.HeavyWeather)) {
                    continue;
                }
                // Run the risk check only if updated_at_reason and updated_at_reason_type are not set
                if (!route?.weatherRoutingInfo?.updated_at_reason && !route?.weatherRoutingInfo?.updated_at_reason_type) {
                    const KmToNM = 1 / 1.852;
                    const pilotIndices = getPilotPointIndeces(route);
                    const [departurePilot, arrivalPilot] = [pilotIndices[0], pilotIndices[pilotIndices.length - 1]];
                    const distancePilotToPilot = pilotIndices.length > 1 ? getRouteDistance(route, departurePilot, arrivalPilot) * KmToNM : 0;

                    if (distancePilotToPilot > 400) {
                        const isPassingthroughRiskZone = isCrossingWeatherRoutingLines(route);
                        if (isPassingthroughRiskZone) {
                            route.weatherRoutingInfo!.required = isPassingthroughRiskZone;
                            route.weatherRoutingInfo!.required_reason = WniRequiredReason.RiskRoute;
                        }
                    }
                }
            }
        }

        if ((newPlanApprovedRouteID && oldPlanApprovedRouteID === newPlanApprovedRouteID && this.passagePlanRouteChanged(
            plans[0].routes[plans[0].selectedRouteIndex]?.route_geometry ?? [],
            plan.routes[plan.selectedRouteIndex]?.route_geometry ?? []
        )) || ((plan.selectedRouteIndex === -1) && (plan.status === "approved"))) {
            plan.status = "planning";
        }

        if (plan.wr_enabled && plan.selectedRouteIndex !== -1 && plan.routes[plan.selectedRouteIndex]?.weatherRoutingInfo && plan.routes[plan.selectedRouteIndex]?.weatherRoutingInfo?.required) {
            const currentSelectedRouteIndex = plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId);
            const oldCurrentSelectedRouteIndex = oldPlan.routes.findIndex(route => route.routeId === oldPlan.currentSelectedRouteId);
            const selectedRoute = plan.routes[currentSelectedRouteIndex];
            const oldSelectedRoute = oldPlan.routes[oldCurrentSelectedRouteIndex];
            const isWeatherRoutebeingAccepted = selectedRoute?.routeCreatedBy === 'WR' && oldSelectedRoute?.routeCreatedBy !== 'WR' && selectedRoute?.weatherRoutingInfo?.status === WRStatus.Accept;

            let isRegularUpdate = true;

            if (selectedRoute && oldSelectedRoute && !isWeatherRoutebeingAccepted) {
                if (!compareRoutes(plan.routes[currentSelectedRouteIndex], oldPlan.routes[oldCurrentSelectedRouteIndex])) {
                    plan.routes[plan.selectedRouteIndex].weatherRoutingInfo!.updated_at = new Date().toISOString();
                    plan.routes[plan.selectedRouteIndex].weatherRoutingInfo!.updated_at_reason = 'StarConnect Route change';
                    plan.routes[plan.selectedRouteIndex].weatherRoutingInfo!.updated_at_reason_type = 'Manual';
                    plan.routes[plan.selectedRouteIndex].weatherRoutingInfo!.status = WRStatus.Read;

                    isRegularUpdate = false;
                }
            }

            if (isRegularUpdate && plan.status === "approved" && !isWrManualUpdateRequest && plan.routes[plan.selectedRouteIndex].weatherRoutingInfo!.updated_at_reason) {
                plan.routes[plan.selectedRouteIndex].weatherRoutingInfo!.updated_at_reason = 'Regular';
                plan.routes[plan.selectedRouteIndex].weatherRoutingInfo!.updated_at_reason_type = 'Manual';
            }
        }

        try {
            this.dispatchEvent(new CustomEvent(PassagePlanEvents.UpdatingPlan));
            await VesselPlansService.updatePlan(plan, oldPlan.id!, sendToShore).then(() => {
                this.dispatchEvent(new CustomEvent(PassagePlanEvents.UpdatingPlanCompleted));
            });
            const newPlan = await VesselPlansService.getPlanById(oldPlan.id!);

            if (newPlan) {
                //updating the plan in indexed-db each time user updates the plan
                if(isVesselOnCloud){
                    await IndexedDBService.plans.update(oldPlan.id,plan);
                }
                const updatedPlanWithId: PlanWithId = { ...newPlan, id: oldPlan.id! };
                this._plans.get(scheduleId)!.plans[0] = updatedPlanWithId;
                this.dispatchEvent(new CustomEvent(SuggestedRouteEvents.PlanChanged, { detail: { scheduleId } }));
            }
        } catch (error) {
            console.error("Failed to update the plan:", error);
        }
        this._updateExecutingPlanPromise = this._updateExecutingPlan();
    }

    async calculatePlan(plan: VoyagePlan): Promise<VoyagePlan> {
        return VesselPlansService.calculatePlan(plan);
    }

    async recalculatePlan(scheduleId: string, plan: VoyagePlan): Promise<void> {
        this.dispatchEvent(new CustomEvent(SuggestedRouteEvents.PlanChanged, { detail: { scheduleId } }));
    }

    async approvePlan(scheduleId: string, hasPilotEtaChanged?: boolean, capturedNotifications?: CapturedPlanNotifications): Promise<void> {
        await this._refreshPlans(scheduleId);
        const plans = this._plans.get(scheduleId)!.plans;

        if (!plans[0].currentSelectedRouteId) {
            throw new Error("Cannot approve plan without a selected route");
        }

        // push the currentSelectedRouteId route to 0th index
        const currentSelectedRouteIndex = plans[0].routes.findIndex(route => route.routeId === plans[0].currentSelectedRouteId);
        const selectedRoute = plans[0].routes[currentSelectedRouteIndex];
        if (currentSelectedRouteIndex > 0) {
            plans[0]!.routes.splice(currentSelectedRouteIndex, 1);
            plans[0]!.routes.unshift(selectedRoute);
        }

        plans[0].selectedRouteIndex = plans[0].routes.findIndex(route => route.routeId === plans[0].currentSelectedRouteId);
        if (plans[0].selectedRouteIndex > -1) {
            const pilotPoints = getPilotPointIndeces(plans[0]!.routes[plans[0].selectedRouteIndex]);
            if (pilotPoints.length > 1) {
                const routeGeometry = plans[0]!.routes[plans[0].selectedRouteIndex].route_geometry;
                plans[0]!.departurePilotPosition = <[number, number]>routeGeometry[pilotPoints[0]].geometry.coordinates;
                plans[0]!.arrivalPilotPosition = <[number, number]>routeGeometry[pilotPoints[pilotPoints.length - 1]].geometry.coordinates;
            }
        }

        // update the routeId
        if (plans[0]?.routes[plans[0].selectedRouteIndex]?.routeId) {
            plans[0].routeId = plans[0].routes[plans[0].selectedRouteIndex].routeId;
        }

        // update the routeIdParent if the selected route has a routeIdparent
        // this is needed to keep track of the parent route when the selected route is a subroute(audit trail)
        if (plans[0]?.routes[plans[0].selectedRouteIndex]?.routeIdParent) {
            plans[0].routeIdParent = plans[0].routes[plans[0].selectedRouteIndex].routeIdParent;
        } else {
            // if the selected route does not have any parent, set the routeIdParent to null
            // this is to remove the already existing routeIdParent (if it is there) from the plan
            plans[0].routeIdParent = null;
        }

        // update the routeId
        if (plans[0]?.routes[plans[0].selectedRouteIndex]?.routeId) {
            plans[0].routeId = plans[0].routes[plans[0].selectedRouteIndex].routeId;
        }

        // update the routeIdParent if the selected route has a routeIdparent
        // this is needed to keep track of the parent route when the selected route is a subroute(audit trail)
        if (plans[0]?.routes[plans[0].selectedRouteIndex]?.routeIdParent) {
            plans[0].routeIdParent = plans[0].routes[plans[0].selectedRouteIndex].routeIdParent;
        } else {
            // if the selected route does not have any parent, set the routeIdParent to null
            // this is to remove the already existing routeIdParent (if it is there) from the plan
            plans[0].routeIdParent = null;
        }

        // Overwrite current plan
        plans[0].status = "approved";

        let updatedAtReason = plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo?.updated_at_reason;
        const updatedAt = plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo?.updated_at;
        const requiredReason = plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo?.required_reason;
        const receivedAt = plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo?.received_at;
        const hasSCRouteChangedAfterWrAdvise = updatedAtReason === 'StarConnect Route change' && updatedAt && receivedAt && new Date(updatedAt).getTime() > new Date(receivedAt).getTime();

        if (plans[0].routes[plans[0].selectedRouteIndex].routeCreatedBy === 'WR' && !hasSCRouteChangedAfterWrAdvise) {
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at = new Date().toISOString();
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at_reason = 'Master Accepted';
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at_reason_type = 'Manual';
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.audit_id = plans[0].audit_id;
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.required = true;
            updatedAtReason = 'Master Accepted';

            // If two or more points has speed change flag (WNI speed flag flow) and plan ETA is < last wr_timestamp, update plan ETA to 5 mins + last wr_timestamp to account for WNI's speed change request
            const wrRouteGeometry = plans[0].routes[plans[0].selectedRouteIndex].route_geometry;
            const speedChangePoints = wrRouteGeometry.filter(f => f.geometry.type === 'Point' && f.properties?.change_speed === "true" ) as VoyageRoutePointFeature[];
            if (speedChangePoints.length >= 2) {
                const lastPointWithWrTimestamp = [...wrRouteGeometry].reverse().find(f => f.geometry.type === 'Point' && f.properties?.wr_timestamp) as VoyageRoutePointFeature | undefined;
                if (lastPointWithWrTimestamp?.properties?.wr_timestamp) {
                    const lastWrTimestamp = new Date(lastPointWithWrTimestamp.properties.wr_timestamp.replaceAll('/', '-') + 'Z').getTime();
                    if (plans[0].eta < lastWrTimestamp) {
                        plans[0].eta = lastWrTimestamp + 5 * 60 * 1000;
                    }
                }
            }

            try {
                const wrAdviseId = plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo?.id;
                if (wrAdviseId) {
                    const auditId = plans[0].audit_id;
                    const routeWrInfo = plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!;
                    const weatherRoutingInfo: WeatherRoutingInfo = {
                        ...mergeWrInfoForStatusUpdate(plans[0], routeWrInfo),
                        audit_id: auditId,
                    };
                    VoyageWeatherRoutingModel!.updateWRStatus(wrAdviseId.toString(), WRStatus.Accept, weatherRoutingInfo);
                }
            } catch (error) {
                console.error("Error handling accept action:", error);
            }
        }
        if (this._WeatherRoutingOn &&  plans[0].wr_enabled) {
            try {
                // create notification for departure report if the pilotetd is less than current time
                if (this.checkIfPilotEtdIsLessThanCurrentTime(plans[0].departurePilotEtd)) {
                    const shouldCreateDepartureReportNotification = await WeatherRoutingModel!.shouldCreateDepartureReportNotification();
                    if (shouldCreateDepartureReportNotification) {
                        const departureReport = await WeatherRoutingModel!.checkIfReportExists(plans[0].scheduleId, 'Departure');
                        if (!departureReport?.isSubmitted) {
                            VesselNotificationsService.createWniReportNotification(
                                AlertType.WNIReports,
                                plans[0].scheduleId,
                                'Departure'
                            );
                        }
                    }
                }
            } catch (error) {
                console.error("Error handling accept action:", error);
            }
        }

        if (this._WeatherRoutingOn && plans[0].wr_enabled && hasPilotEtaChanged) {
            const popUp = await VoyageDialogPopupManager.createDialog({
                headerText: "Pilot ETA changed",
                bodyText: "Do you want to send the updated plan's ETA to WNI for weather routing?",
                acceptButtonText: "Yes",
                rejectButtonText: "Cancel",
                noCloseButton: true,
            })

            const response = await popUp.getResponse();

            if (response === "accept") {
                plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at = new Date().toISOString();
                plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at_reason = 'RTA Change';
                plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at_reason_type = 'Manual';
                plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.status = WRStatus.Read;
                plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.required = true;
            }
        } else {
            if (plans[0].wr_enabled && updatedAtReason && updatedAtReason !== 'StarConnect Route change' && updatedAtReason !== 'Master Accepted') {
                plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at_reason = 'Regular';
                plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at_reason_type = 'Manual';
                plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.required = true;
            }
        }

        const wrRejected = JSON.parse(localStorage.getItem('wrRejected') || '{}');

        if (wrRejected && wrRejected.scheduleId === plans[0].scheduleId) {
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at_reason = 'Master Rejected';
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at_reason_type = 'Manual';
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.updated_at = new Date().toISOString();
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.status = 'reject';
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.remarks = `${wrRejected.reason} - ${wrRejected.comment}`;
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.correlationId = wrRejected.correlationId;
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.audit_id = plans[0].audit_id;
            plans[0].routes[plans[0].selectedRouteIndex].weatherRoutingInfo!.required = true;

            localStorage.removeItem('wrRejected');
            VesselAlertsModel!.removeAlertsByType((<any>window)["appenv"].vesselIMONumber, AlertType.CounterProposalNotification);
        }

        // Attach the send-and-approve notification payloads (BprReject / Route
        // selection) to the plan metadata BEFORE the update, so the approved plan
        // sent to shore is self-contained and no join against the notifications
        // service is needed. Never blocks approval: payloads carry a captureError
        // field when their collection failed.
        if (capturedNotifications?.bprReject || capturedNotifications?.routeSelection) {
            plans[0].metadata = {
                ...(plans[0].metadata ?? {}),
                ...(capturedNotifications.bprReject ? { bprReject: capturedNotifications.bprReject } : {}),
                ...(capturedNotifications.routeSelection ? { routeSelection: capturedNotifications.routeSelection } : {}),
            };
        }

        const plansWithId = this.ensureIdIsDefined(plans);
        await VesselPlansService.updatePlan(plansWithId[0], plansWithId[0].id, true);
        const newPlan = await VesselPlansService.getPlanById(plans[0].id!);

        //updating the plan in indexed-db each time user updates the plan / in this context approves the plan
        if(isVesselOnCloud) {
            await IndexedDBService.plans.update(plansWithId[0].id,plansWithId[0]);
        }

        if(newPlan){
            const updatedPlanWithId: PlanWithId = { ...newPlan, id: newPlan.id! }; 
                    this._plans.get(scheduleId)!.plans[0] = updatedPlanWithId;
                    this.dispatchEvent(new CustomEvent(SuggestedRouteEvents.PlanChanged, { detail: { scheduleId } }));
        }


        this.dispatchEvent(new CustomEvent(SuggestedRouteEvents.PlanChanged, { detail: { scheduleId } }));
        this._updateExecutingPlanPromise = this._updateExecutingPlan();
    }

    private checkIfPilotEtdIsLessThanCurrentTime(pilotEtd: number): boolean {
        const currentTime = new Date().getTime();
        const pilotEtdTime = new Date(pilotEtd).getTime();
        return pilotEtdTime < currentTime;
    }

    async updateRouteBprAdoption(scheduleId: string, routeId: string, bprAdoption: BprAdoption): Promise<void> {
        const plansEntry = this._plans.get(scheduleId);
        if (!plansEntry?.plans?.[0]) {
            console.error('No plan found for scheduleId when updating bprAdoption:', scheduleId);
            return;
        }

        const plan = plansEntry.plans[0];
        const route = plan.routes.find(r => r.routeId === routeId);
        if (!route) {
            console.error('No route found for routeId when updating bprAdoption:', routeId, scheduleId);
            return;
        }

        route.bprAdoption = bprAdoption;

        const plansWithId = this.ensureIdIsDefined([plan]);
        await VesselPlansService.updatePlan(plansWithId[0], plansWithId[0].id, false);

        if (isVesselOnCloud) {
            await IndexedDBService.plans.update(plansWithId[0].id, plansWithId[0]);
        }
    }

    ensureIdIsDefined(plans: VoyagePlan[]): PlanWithId[] {
        return plans.map(plan => {
            if (plan.id === undefined) {
                throw new Error("Plan id is undefined");
            }
            return plan as PlanWithId;
        });
    }

    async getVesselCharacteristics(): Promise<VesselCharacteristics | null> {
        if (this._vesselCharacteristics) {
            return this._vesselCharacteristics;
        }

        this._vesselCharacteristics = await VesselPlansService.getVesselCharacteristics();

        return this._vesselCharacteristics;
    }

    async setVesselCharacteristics(characteristics: VesselCharacteristics): Promise<void> {
        await VesselPlansService.setVesselCharacteristics(characteristics);
        this._vesselCharacteristics = characteristics;
        this.dispatchEvent(new CustomEvent("vessel_characteristics_updated"));
    }

    passagePlanChanged(oldPlan: VoyagePlan, newPlan: VoyagePlan): boolean {

        const imo = isVesselOnCloud ? VoyageImoModel!.imo! : (<any>window)["appenv"].vesselIMONumber;
        // Check selected route
        if (oldPlan.selectedRouteIndex !== newPlan.selectedRouteIndex) {
            if (oldPlan.status === "approved") {
                faro.api.pushEvent("approved-plan-status-change", {
                    imo: `${imo}`,
                    trigger: "selectedRouteIndex",
                    old_selectedRouteIndex: `${oldPlan.selectedRouteIndex}`,
                    new_selectedRouteIndex: `${newPlan.selectedRouteIndex}`,
                    
                });
            }
            // update sailing directions, nav areas and last update date when the route has changed
            if (newPlan.routes[newPlan.selectedRouteIndex] && newPlan.routes[newPlan.selectedRouteIndex].route_geometry) {
                if (VesselRs1345Model && VesselRs6Model) {
                    const latestRs1345 = VesselRs1345Model.latestUpdateDate;
                    const latestRs6 = VesselRs6Model.latestUpdateDate;

                    if (latestRs1345 && latestRs6) {
                        const latestDate = getLatestWeekDate([latestRs6, latestRs1345])!.split('/');
                        newPlan.routes[newPlan.selectedRouteIndex].passagePlan!.pubCorrectedToWeek = `Week ${latestDate[0]}, ${latestDate[1]}`; // in ex. Week 23, 2021
                    }
                }

                const route = newPlan.routes[newPlan.selectedRouteIndex];
                const routeGeometry = newPlan.routes[newPlan.selectedRouteIndex].route_geometry;
                // Prioritize user input. 
                // If value is system input, refresh the data from the system as the system data could have changed based on new route
                const sailingDirections = DataExtractor.getUserOrSystemInputFromRoutePassagePlan(route, "sailingDirections");
                newPlan.routes[newPlan.selectedRouteIndex].passagePlan!.sailingDirections
                    = sailingDirections.type === "system"
                        ? VesselEnpBookAndChapterModel!.getCrossedEnpChaptersAndBooks(routeGeometry)
                        : sailingDirections.value;
                newPlan.routes[newPlan.selectedRouteIndex].passagePlan!.weatherInfo.navareaRegions = VesselNavAreasModel!.getCrossedNavAreas(routeGeometry);
                const weatherInfoNavareaRegions = DataExtractor.getUserOrSystemInputFromRoutePassagePlan(route, "weatherInfoNavareaRegions");
                newPlan.routes[newPlan.selectedRouteIndex].passagePlan!.safetyMessages.navareaRegions
                    = weatherInfoNavareaRegions.type === "system"
                        ? VesselNavAreasModel!.getCrossedNavAreas(routeGeometry)
                        : weatherInfoNavareaRegions.value;

                const weatherInfoNavtexStations = DataExtractor.getUserOrSystemInputFromRoutePassagePlan(route, "weatherInfoNavtexStations");
                newPlan.routes[newPlan.selectedRouteIndex].passagePlan!.weatherInfo.navtexStations
                    = weatherInfoNavtexStations.type === "system"
                        ? VesselRs1345Model!.getWeatherStationsCrossingRoute(routeGeometry)
                        : weatherInfoNavtexStations.value;

                const safetyMessagesNavtexStations = DataExtractor.getUserOrSystemInputFromRoutePassagePlan(route, "weatherInfoNavtexStations");
                newPlan.routes[newPlan.selectedRouteIndex].passagePlan!.safetyMessages.navtexStations
                    = weatherInfoNavtexStations.type === "system"
                        ? VesselRs1345Model!.getNavWarningStationsCrossingRoute(routeGeometry)
                        : weatherInfoNavtexStations.value;
            }

            return true;
        }

        //handle schedule change
        if (oldPlan.arrivalTerminalCode !== newPlan.arrivalTerminalCode ||
            oldPlan.departureTerminalCode !== newPlan.departureTerminalCode) {
            // clear arrival and departure information in passage plan when schedule has been changed
            newPlan.passagePlan.arrivalPort = "";
            newPlan.passagePlan.departurePort = "";
            newPlan.passagePlan.portMemo = "";
            if (oldPlan.status === "approved") {
                faro.api.pushEvent("approved-plan-status-change", {
                    imo: `${imo}`,
                    trigger: "terminalCodeChange",
                    old_arrivalTerminalCode: `${oldPlan.arrivalTerminalCode}`,
                    new_arrivalTerminalCode: `${newPlan.arrivalTerminalCode}`,
                    old_departureTerminalCode: `${oldPlan.departureTerminalCode}`,
                    new_departureTerminalCode: `${newPlan.departureTerminalCode}`,
                });
            }
            return true;
        }


        const oldRoute: VoyageRoute | undefined = oldPlan.routes[oldPlan.selectedRouteIndex];
        const oldPPRoute = oldRoute ? this._extractPassagePlanRoute(oldRoute.route_geometry) : [];
        const newRoute: VoyageRoute | undefined = newPlan.routes[newPlan.selectedRouteIndex];
        const newPPRoute = newRoute ? this._extractPassagePlanRoute(newRoute.route_geometry) : [];
        if (!deepEqual(oldPPRoute, newPPRoute)) {
            if (oldPlan.status === "approved") {
                try {
                    const eventData = {
                        imo: `${imo}`,
                        trigger: "routeGeometryChange",
                        old_routeId: `${oldRoute?.routeId}`,
                        new_routeId: `${newRoute?.routeId}`,
                    };
                    faro.api.pushEvent("approved-plan-status-change", eventData);
                    console.log("Faro event pushed - routeGeometryChange:", eventData);
                } catch (error) {
                    console.error("Failed to push Faro event:", error);
                }
            }
            return true;
        }

        return false
    }

    passagePlanRouteChanged(oldRoute: Array<VoyageRouteLineFeature | VoyageRoutePointFeature>,
        newRoute: Array<VoyageRouteLineFeature | VoyageRoutePointFeature>): boolean {
        const extractKeyProps = (route: Array<VoyageRouteLineFeature | VoyageRoutePointFeature>) => {
            const result = [];
            for (let i = 0; i < route.length - 1; i++) {
                const feature = route[i];
                if (feature.geometry.type === "Point" && feature.properties.type === "Hard") {
                    result.push({
                        coordinates: feature.geometry.coordinates,
                        legType: route[i + 1].properties.leg_type === "Rhumbline" ? "RL" : "GC",
                    });
                }
            }
            return result;
        };

        return !deepEqual(extractKeyProps(oldRoute), extractKeyProps(newRoute));
    }

    approvalReminder(message: string, autoApprove?: boolean): void {
        if (autoApprove) {
            return
        }

        VoyageDialogPopupManager.createDialog({
            headerText: "Latest passage plan not approved",
            bodyText: message,
            acceptButtonText: "Close"
        });
        this._missingApprovedPlanAlreadyPrompted = true;
    }

    private _plans: Map<string, PlansAndTime> = new Map();
    private _executingPlanScheduleId: string = "";
    private _executingPlan: VoyagePlan | null = null;
    private _createRequests: Map<string, Promise<number>> = new Map();
    private _vesselCharacteristics: VesselCharacteristics | null = null;
    private _loadPlansPromise: Promise<void>;
    private _updateExecutingPlanPromise: Promise<void>;
    private _rollPopupIsShowing: boolean = false;
    private _missingApprovedPlanAlreadyPrompted: boolean = false;
    private _debouncedCheckAndShowRollWarning;


    private async _updateExecutingPlan(): Promise<void> {
        await this._loadPlansPromise;

        // check if vessel passed pilot point on executing plan and issue a weather routing warning
        // commenting it out as it is hanlded in WR flow
        // if (this._executingPlan) {
        //     handleWeatherRoutingPilotPoint(this._executingPlan);
        // }

        let oldExecutingPlan = this._executingPlan;

        let executingScheduleId = "";
        const schedule = await VoyageSchedulesModel!.getScheduleFor(VoyageImoModel!.imo!);

        if (schedule) {
            const activeEntry = schedule.find(entry => entry.active);
            if (activeEntry) {
                executingScheduleId = activeEntry.id;
            }
        }

        if (executingScheduleId !== this._executingPlanScheduleId) {
            this._missingApprovedPlanAlreadyPrompted = false;
            this._executingPlanScheduleId = executingScheduleId;
        }

        if (this._executingPlanScheduleId) {
            // Check if we have a new plan, and if that plan has been auto-updated
            await this._refreshPlans(this._executingPlanScheduleId);
            this._executingPlan = await this.getApprovedPlan(this._executingPlanScheduleId);
        }
        else {
            this._executingPlan = null;
        }

        if (!deepEqual(oldExecutingPlan, this._executingPlan)) {
            if (this._executingPlan) {

                // once called dont call for next 1 minute
                this._debouncedCheckAndShowRollWarning();
                if (this._executingPlan.autoUpdateReason) {
                    this._addAutoUpdateAlert(oldExecutingPlan);
                    await verifyRequestedETAs(this._executingPlan.routes[this._executingPlan.selectedRouteIndex], this._executingPlan, this._executingPlan.scheduleId, this._executingPlan?.selectedRouteIndex);
                }
            }
            this.dispatchEvent(new CustomEvent('executing-plan-changed'));
        }
    }


    private async _checkAndShowRollWarning(): Promise<void> {
        if (this._rollPopupIsShowing) {
            return;
        }

        await VesselNotificationsModel?.loadWeatherAlarms();

        const heavyWeatherNotification = VesselNotificationsModel?.getWeatherAlarm(NotificationTypes.HeavyWeather);
        const parametricRollNotification = VesselNotificationsModel?.getWeatherAlarm(NotificationTypes.ParametricRoll);
        const heavyRollingNotification = VesselNotificationsModel?.getWeatherAlarm(NotificationTypes.HeavyRolling);
        const highAccelerationForceNotification = VesselNotificationsModel?.getWeatherAlarm(NotificationTypes.HighAccelerationForce);

        if (heavyWeatherNotification && 'details' in heavyWeatherNotification) {
            this._showweatherAlerts("hwaAlarm", heavyWeatherNotification.details, heavyWeatherNotification.id);
        }
        if (parametricRollNotification && 'details' in parametricRollNotification) {
            this._showweatherAlerts("parametricAlarm", parametricRollNotification.details, parametricRollNotification.id);
        }
        if (heavyRollingNotification && 'details' in heavyRollingNotification) {
            this.dispatchEvent(new CustomEvent('heavy-rolling-alarm', {
                detail: {
                    data: heavyRollingNotification.details,
                    id: heavyRollingNotification.id
                },
                bubbles: true,
                composed: true
            }));
        }
        if (highAccelerationForceNotification && 'details' in highAccelerationForceNotification) {
            this.dispatchEvent(new CustomEvent('high-acceleration-force-alarm', {
                detail: {
                    data: highAccelerationForceNotification.details,
                    id: highAccelerationForceNotification.id
                },
                bubbles: true,
                composed: true
            }));
        }

    }

    private getHeavyWeatherViolationsText (type: string, data: any,): string  {
        const violations: HeavyWeatherViolation[] =
            data.metadata?.heavy_weather?.violations ?? [];

        if (!Array.isArray(violations) || violations.length === 0) {
            return "";
        }

        return violations
            .map(
            ({ parameter, forecast_value, company_limit_value }) =>
                `Threshold exceeded ${parameter} Forecast ${forecast_value}/Company Limits ${company_limit_value}`
            )
            .join("; ");
        };

    async _showweatherAlerts(type: string, data: any, id: string | number) {
        let timeCriticality = 0;
        let duration = 0;
        let durationInHours = 0;
        timeCriticality = (Number(new Date(data.start_time)) - Number(new Date())) / (1000 * 60 * 60);
        if (type === "parametricAlarm") {
            duration = Number(new Date(data.end_time)) - Number(new Date(data.start_time));
            durationInHours = duration / (1000 * 60 * 60);
        }
        const alarmType = type === "hwaAlarm" ? "Heavy Weather " : "Parametric Roll ";
        const warningSummaryText = type === "hwaAlarm" ? `The vessel is currently approaching an area with foreseen Heavy Weather exceeding Maersk’s policy. The vessel would reach this area at ${GetFormattedDate(data.start_time)}.` :
            `The vessel is currently approaching an area with increased risk of parametric roll. The vessel would reach this area at ${GetFormattedDate(data.start_time)} and is expected to last for ${Math.round(durationInHours)} hours.`;
        const violationsText = type === "hwaAlarm" ? this.getHeavyWeatherViolationsText(type, data) : "";
        const warningSummary = warningSummaryText + `\n\nPlease consider asking for a weather routing advice or an update.` + (violationsText ? `\n\n${violationsText}` : "");
        if ((timeCriticality <= 24 && type === "hwaAlarm") || (timeCriticality <= 48 && type === "parametricAlarm" && durationInHours >= 3)) {
            const warningElement = new VoyageDialogPopupBodyWarning;
            warningElement.icon = SvgIcons.exclamationTriangle;
            warningElement.warningSummary = warningSummary;

            const warningPopup = VoyageDialogPopupManager.createDialog({
                headerText: `Risk of ${alarmType}`,
                bodyContent: warningElement,
                acceptButtonText: "Acknowledge"
            });

            this._rollPopupIsShowing = true;

            // work-around to handle multiple PC issue - will make get api call to see if the alarm is still Active before changeing the status to in-progress

            if ((await warningPopup.getResponse()) === "accept") {
                const latestAlarmData = await VesselAlarmsModel?.getNotificationData(id);

                if (latestAlarmData && latestAlarmData.state === AlarmState.Active) {
                    this._rollPopupIsShowing = false;
                    const notificationData = {
                        state: "in-progress",
                        data: JSON.stringify({
                            reason: "Vessel acknowledged the risk",
                        })
                    }
                    VesselAlarmsModel?.postAlarmsReasons(notificationData, id);
                }
                else {
                    this._rollPopupIsShowing = false;
                }
            }
        }
        const rollETA = data.start_time;
        const [date, time] = GetFormattedDate(rollETA).split(' ');
        const datePart = `${date} ${GetFormattedDate(rollETA).split(' ')[1]} ${GetFormattedDate(rollETA).split(' ')[2]}`;
        const timePart = GetFormattedDate(rollETA).split(' ')[3];
        const message = type === "hwaAlarm" ? `Risk of Heavy weather foreseen on ${datePart} at ${timePart}.` : `Risk of parametric roll foreseen on ${datePart} at ${timePart} for ${Math.round(durationInHours)} hours.`;
        VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, {
            message: message,
            id: type === "hwaAlarm" ? AlertType.HeavyWeather : AlertType.ParametricRoll
        });
    }

    private _consecutiveRollSegmentsEndCoords(features: RouteGeometry, currentLineIndex: number, nextLine?: boolean, previousSegmentEndCoords?: [number, number]): [number, number] {
        const parametricRoll = features[currentLineIndex].properties.warnings.parametricRoll;

        // check for next consecutive roll segment
        if (nextLine && previousSegmentEndCoords) {
            if (!parametricRoll || parametricRoll.length < 1) {
                return previousSegmentEndCoords;
            }

            const segmentStartXCoord = features[currentLineIndex].properties.warnings.parametricRoll[0].coordinates[0][0];
            const segmentStartYCoord = features[currentLineIndex].properties.warnings.parametricRoll[0].coordinates[0][1];
            const lineStartXCoord = (<VoyageRouteLineFeature>features[currentLineIndex]).geometry.coordinates[0][0];
            const lineStartYCoord = (<VoyageRouteLineFeature>features[currentLineIndex]).geometry.coordinates[0][1];

            // if current segment does not start from previous segment, it's not consecutive
            if (segmentStartXCoord !== lineStartXCoord || segmentStartYCoord !== lineStartYCoord) {
                return previousSegmentEndCoords;
            }
        }

        const segmentEndXCoord = features[currentLineIndex].properties.warnings.parametricRoll[0].coordinates[1][0];
        const segmentEndYCoord = features[currentLineIndex].properties.warnings.parametricRoll[0].coordinates[1][1];
        const lineEndXCoord = (<VoyageRouteLineFeature>features[currentLineIndex]).geometry.coordinates[1][0];
        const lineEndYCoord = (<VoyageRouteLineFeature>features[currentLineIndex]).geometry.coordinates[1][1];

        // if segment reaches end of line, continue searching for consecutive segments on next linestring
        if (segmentEndXCoord === lineEndXCoord && segmentEndYCoord === lineEndYCoord) {
            return this._consecutiveRollSegmentsEndCoords(features, currentLineIndex + 2, true, [segmentEndXCoord, segmentEndYCoord])
        }

        return [segmentEndXCoord, segmentEndYCoord]
    }

    private async _refreshPlans(scheduleId: string): Promise<void> {
        await this._loadPlansPromise;
        const plansAndTime = this._plans.get(scheduleId);
        if (
            plansAndTime &&
            Date.now() - plansAndTime.fetchedTime <= PlansStaleAge
        ) {
            // Fetched recently; do nothing
            return
        }

        const plans = await VesselPlansService.getPlansByScheduleId([
            scheduleId,
        ])

        this._plans.set(scheduleId, { fetchedTime: Date.now(), plans });
        if (scheduleId === this._executingPlanScheduleId) {
            this._updateExecutingPlanPromise = this._updateExecutingPlan();
            await this._updateExecutingPlanPromise;
        }
    }

    private async _createNewPlanEntry(plan: PlanWithId) {
        if (this._createRequests.has(plan.scheduleId)) {
            // Request already in progress, wait for it to complete
            await this._createRequests.get(plan.scheduleId)
            return
        }

        this._createRequests.set(
            plan.scheduleId,
            VesselPlansService.createPlan(plan),
        )
        plan.id = await this._createRequests.get(plan.scheduleId)!
        this._createRequests.delete(plan.scheduleId)

        if (this._plans.has(plan.scheduleId)) {
            this._plans.get(plan.scheduleId)!.plans.unshift(plan)
        } else {
            this._plans.set(plan.scheduleId, {
                fetchedTime: Date.now(),
                plans: [plan],
            })
        }

        this.dispatchEvent(
            new CustomEvent(SuggestedRouteEvents.PlanChanged, {
                detail: { scheduleId: plan.scheduleId },
            }),
        )
    }

    private async _onPositionUpdated() {
        const position = VesselPositionModel!.position;
        if (this._executingPlan && position && position.source === "manual" &&
            new Date(position.timestamp).getTime() + 1000 * 60 > Date.now()) {
            const route = deepCopy(this._executingPlan.routes[this._executingPlan.selectedRouteIndex]);
            // Recalculate executing plan if the weather forecast is outdated
            if (route.route_info!.calculationInput.forecast_versions[0] !== WeatherModel!.latestForecastVersion) {
                const uiRouteIndex = this._executingPlan.routes.findIndex(r => r.routeId === route.routeId);
                const calcOptions: RoutingJobOptions = {
                    routeId: route.routeId,
                    uiRouteIndex: uiRouteIndex !== -1 ? uiRouteIndex : undefined,
                    planScheduleId: this._executingPlan.scheduleId,
                    draftFore: this._executingPlan.foreDraft,
                    draftAft: this._executingPlan.aftDraft,
                    gmt: this._executingPlan.gmt !== null ? this._executingPlan.gmt : undefined,
                    etd: this._executingPlan.etd,
                    eta: this._executingPlan.eta,
                    rollAngle: this._executingPlan.rollAngle !== null ? this._executingPlan.rollAngle : undefined,
                    numberOfReefers: this._executingPlan.numberOfReefers,
                    routeType: "WeatherOptimized",
                    whaleZonesApplied: route.route_info!.whaleZonesApplied,
                    disableWhaleZoneSpeedRestrictions: route.route_info!.disableWhaleZoneSpeedRestrictions,
                    passagePlan: route.passagePlan,
                    weatherRoutingInfo: route?.weatherRoutingInfo,
                    solidGm: this._executingPlan.solidGm !== null ? this._executingPlan.solidGm : undefined,
                }

                // Mark passed waypoints, add current position and calculate from now
                try {
                    // only add position as waypoint and mark passed if the plan's etd is in the past
                    if (this._executingPlan.etd < new Date().getTime()) {
                        await markPassedAndAddCurrentPosition(route);
                    }
                }
                catch (err) {
                    console.warn("Failed to update route with current position, aborting re-calc: ", (<Error>err).message);
                    return;
                }

                calcOptions.etd = Date.now();

                const newRoute = await VoyageRoutingModel!.postRoutingJobUsingRoute(route, calcOptions);

                // Save to plan, setting autoUpdateReason
                const newPlan: PlanWithId = JSON.parse(JSON.stringify(this._executingPlan));
                newPlan.autoUpdateReason = "NewForecastManualPosition";
                newPlan.routes[newPlan.selectedRouteIndex] = newRoute;

                await this._createNewPlanEntry(JSON.parse(JSON.stringify(newPlan)));
                this._updateExecutingPlanPromise = this._updateExecutingPlan();
            }
        }
    }

    private async _onScheduleChanged() {
        // Plans may have been remapped - reload everything
        this._loadPlansPromise = this._loadPlans();
        this._updateExecutingPlanPromise = this._updateExecutingPlan();

        // Remind user that the newest plan of the executing voyage is not approved
        this._currentPlanOfExecutingVoyageApprovalReminder();
    }

    private _deepSealObject(argument: { [index: string]: any }): void {
        // Seal object and any properties that are also an object
        if (argument !== null && !Array.isArray(argument) && typeof (argument) === "object") {
            Object.seal(argument);
            for (const property of Object.values(argument)) {
                this._deepSealObject(property);
            }
        }
    }

    private _createPlanObject(inputPlan: PlanWithId): VoyagePlan {
        const plan = JSON.parse(JSON.stringify(inputPlan))
        if (plan.passagePlan.notEcdisCompliant === undefined) {
            plan.passagePlan.notEcdisCompliant = false
        }

        // While the Audit Route properties are implemented as a new feature, verify that these properties are present here.
        // If they are missing, they might not be persisted in the backend.
        if (
            plan.routeId === undefined ||
            plan.routeIdInitial === undefined ||
            plan.routeIdParent === undefined
        ) {
            console.warn(
                `Audit route props are missing - routeId: ${plan.routeId}, routeIdInitial: ${plan.routeIdInitial}, routeIdParent: ${plan.routeIdParent}`,
            )
        }

        Object.defineProperty(plan, 'scheduleId', { writable: false })
        // Ensure passagePlan.userInput exists as an own property before sealing,
        // otherwise assigning to it later (e.g. in LoadStar accept) will throw.
        if (!plan.passagePlan.userInput) {
            plan.passagePlan.userInput = {}
        }
        this._deepSealObject(plan)
        return plan
    }

    private _getBlankPlan(scheduleId: string): PlanWithId {
        return {
            id: -1,
            routeId: null,
            routeIdParent: null,
            routeIdInitial: null,
            scheduleId,
            etd: -1,
            eta: -1,
            departureTerminalCode: '',
            startPosition: null,
            departurePilotPosition: null,
            departurePilotEtd: -1,
            arrivalTerminalCode: '',
            endPosition: null,
            arrivalPilotPosition: null,
            arrivalPilotEta: -1,
            foreDraft: -1,
            aftDraft: -1,
            gmt: null,
            solidGm: null,
            rollAngle: null,
            numberOfReefers: -1,
            status: 'planning',
            passagePlan: {
                description: '',
                departurePort: '',
                arrivalPort: '',
                portMemo: '',
                reminders: '',
                notEcdisCompliant: false,
                encCellsUsed: '',
                booksAndTables: '',
                chartsAndLightsNotes: '',
                sailingDirections: '',
                listOfRadioSignals: '',
                listOfLights: '',
                gpsEcdisRadarMap: '',
                paperCharts: '',
                pubCorrectedToWeek: '',
                tideTables: '',
                publicationsNotes: '',
                mastersOrders: '',
                positionValidation: '',
                captain: '',
                navigationalOfficer: '',
                planDate: null,
                drafts: {
                    berthToPilot: {
                        foreDraft: null,
                        aftDraft: null,
                        airDraft: null,
                        swDensity: null,
                    },
                    pilotToPilot: {
                        foreDraft: null,
                        aftDraft: null,
                        airDraft: null,
                        swDensity: null,
                    },
                    pilotToBerth: {
                        foreDraft: null,
                        aftDraft: null,
                        airDraft: null,
                        swDensity: null,
                    },
                },
                squats: {
                    minSpeed: {
                        openWaters: null,
                        confinedWaters: null,
                    },
                    maxSpeed: {
                        openWaters: null,
                        confinedWaters: null,
                    },
                    bufferSpeed: {
                        openWaters: null,
                        confinedWaters: null,
                    },
                    vesselMaxSpeed: {
                        openWaters: null,
                        confinedWaters: null,
                    },
                },
                underKeelClearance: {
                    confinedWater: {
                        required: '',
                        minimumExpected: null,
                    },
                    openWater: {
                        required: '',
                        minimumExpected: null,
                    },
                    alongsideDeparture: {
                        required: '',
                        minimumExpected: null,
                    },
                    alongsideArrival: {
                        required: '',
                        minimumExpected: null,
                    },
                },
                safetyMessages: {
                    navareaRegions: '',
                    navtexStations: '',
                },
                weatherInfo: {
                    navareaRegions: '',
                    navtexStations: '',
                    info: '',
                },
                equipmentStatus: {
                    operationalCondition: '',
                    failures: '',
                },
                bridgeTeam: {
                    departure: {
                        master: false,
                        chiefOfficer: false,
                        secondOfficer: false,
                        thirdOfficer: false,
                        pilot: false,
                        helmsman: false,
                        lookout1: false,
                        lookout2: false,
                    },
                    arrival: {
                        master: false,
                        chiefOfficer: false,
                        secondOfficer: false,
                        thirdOfficer: false,
                        pilot: false,
                        helmsman: false,
                        lookout1: false,
                        lookout2: false,
                    },
                },
                calculatedValues: {
                    avgSpeedOGPilotToPilot: null,
                    routeMinSpeed: null,
                    routeMaxSpeed: null,
                    routeAverageSpeed: null,
                    berthToPilotDistance: null,
                    pilotToPilotDistance: null,
                    pilotToBerthDistance: null,
                },
                signatures: [
                    { rank: '', name: '' },
                    { rank: '', name: '' },
                    { rank: '', name: '' },
                    { rank: '', name: '' },
                    { rank: '', name: '' },
                    { rank: '', name: '' },
                ],
                userInput: {},
            },
            routes: [],
            selectedRouteIndex: -1,
            autoUpdateReason: '',
            currentSelectedRouteId: '',
        }
    }

    private _hasChanges(plan: VoyagePlan, planObject: VoyagePlan): boolean {
        // Deeply compare the two objects and return true if they are different
        return !deepEqualPlans(plan, planObject);
    }

    private async _addAutoUpdateAlert(oldExecutingPlan: VoyagePlan | null) {
        switch (this._executingPlan!.autoUpdateReason) {
            case "ETAChange":
                VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, {
                    message: "ETA changed for current voyage. Voyage has been recalculated. Please re-evaluate new power advice.",
                    id: AlertType.PlanRecalculatedETAChange
                });
                break;

            case "NewForecast":
            case "NewForecastManualPosition":
                // Only show alert if change in power is above a threshold of 0.5kt or 1% MCR
                let showAlert = false;
                if (oldExecutingPlan) {
                    try {
                        const newRoute = this._executingPlan!.routes[this._executingPlan!.selectedRouteIndex].route_geometry;
                        const oldRoute = oldExecutingPlan!.routes[this._executingPlan!.selectedRouteIndex].route_geometry;
                        const position = [VesselPositionModel!.position!.longitude!, VesselPositionModel!.position!.latitude!];
                        const nextWp = await VoyageRoutingModel!.getNextWaypointIndex(-1, position, newRoute);
                        const oldPower = oldRoute[nextWp.nextWPIndex - 1].properties.propulsion_power;
                        const newPower = newRoute[nextWp.nextWPIndex - 1].properties.propulsion_power;
                        const MCR = (await VesselPerformanceModel!.getConfigurationData())!.maxPower;
                        if (Math.abs(newPower - oldPower) >= MCR * 0.01) {
                            showAlert = true;
                        }
                        const oldSOG = oldRoute[nextWp.nextWPIndex - 1].properties.speed_over_ground;
                        const newSOG = newRoute[nextWp.nextWPIndex - 1].properties.speed_over_ground;
                        const MPS2KNOTS = (60 * 60) / 1852;
                        if (Math.abs(newSOG - oldSOG) * MPS2KNOTS > 0.5) {
                            showAlert = true;
                        }
                    }
                    catch (e) {
                        console.warn("Failed to calculate power difference", e);
                        // Show alert in this case to be on the safe side
                        showAlert = true;
                    }
                }
                else {
                    showAlert = true;
                }

                if (showAlert) {
                    VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, {
                        message: "Voyage has been recalculated with a new forecast. Please re-evaluate new power advice.",
                        id: AlertType.PlanRecalculatedNewForecast
                    });

                }
                break;

            default:
                console.warn("autoUpdateReason not recognized; using generic alert text");
                VesselAlertsModel!.addAlert((<any>window)["appenv"].vesselIMONumber, {
                    message: "Voyage has been recalculated. Please re-evaluate new power advice.",
                    id: AlertType.PlanRecalculatedGeneric
                });
                break;
        }
    }

    private async _loadPlans() {
        const imoNumber = isVesselOnCloud ? VoyageImoModel!.imo! : (<any>window)["appenv"].vesselIMONumber;
        const scheduleEntries = await VoyageSchedulesModel!.getScheduleFor(imoNumber);
        if (!scheduleEntries) return;
        
        // is cloud
        if(isVesselOnCloud) {
            const terminalIds: string[] = [];
            for (const entry of scheduleEntries)
            {
                terminalIds.push(entry.arrivalTerminalCode); 
            }
            // load terminalPortMemo for all schedule entries
            this.getAllTerminalsPortMemosOnLoad(terminalIds);
        }

        const plans = await VesselPlansService.getPlansByScheduleId(scheduleEntries.map(x => x.id));
        
        // is cloud
        if(plans && isVesselOnCloud && isOnline()){
            this.savePlansToIndexedDb(plans, scheduleEntries);
        }
        this._plans.clear();

        for (const plan of plans) {
            const currentTime = new Date().getTime();
            const etaTime = new Date(plan.eta).getTime();

            if (currentTime >= etaTime) {
                if (localStorage.getItem(plan.id.toString())) {
                    localStorage.removeItem(plan.id.toString());
                }
            }
            // assign correct ports after changing the schedule in the editor
            const scheduleEntry = scheduleEntries.find(se => se.id === plan.scheduleId);
            if (scheduleEntry) {
                if (plan.arrivalTerminalCode !== scheduleEntry.arrivalTerminalCode
                    || plan.departureTerminalCode !== scheduleEntry.departureTerminalCode) {
                    plan.arrivalTerminalCode = scheduleEntry.arrivalTerminalCode;
                    plan.departureTerminalCode = scheduleEntry.departureTerminalCode;
                    plan.passagePlan.arrivalPort = "";
                    plan.passagePlan.departurePort = "";
                    plan.passagePlan.portMemo = "";
                }
            }

            if (!this._plans.has(plan.scheduleId)) {
                this._plans.set(plan.scheduleId, {
                    fetchedTime: Date.now(),
                    plans: [plan]
                });
            }
            else {
                this._plans.get(plan.scheduleId)!.plans.push(plan);
            }
        }

        // Add empty entries for all other schedule entries
        for (const entry of scheduleEntries) {
            if (!this._plans.has(entry.id)) {
                this._plans.set(entry.id, {
                    fetchedTime: Date.now(),
                    plans: []
                });
            }
        }
    }

    async getAllTerminalsPortMemosOnLoad(terminalIds: string[] = []) {
        try {
            await TerminalsModel?.getAllTerminalsPortMemos(terminalIds);
        } catch (error) {
          console.error(error);
        }
    }

    private _extractPassagePlanRoute(route: Array<VoyageRoutePointFeature|VoyageRouteLineFeature>): Array<VoyageRoutePointFeature>
    {
        const relevantProps = ["pilotPoint", "turnrate", "speed_maximum", "speed_minimum", "name", "catzoc",
            "max_x_track", "min_depth", "rot", "tide", "squat"];
        const ppRoute: Array<VoyageRoutePointFeature> = [];

        for (let i = 0; i < route.length - 1; i++) {
            const feature = route[i];
            if (feature.geometry.type === "Point" && feature.properties.type === "Hard") {
                // Add point and relevant properties to output
                const point: VoyageRoutePointFeature = JSON.parse(JSON.stringify(feature));
                point.properties = this._filterProperties(relevantProps, point.properties);
                point.properties.legType = route[i + 1].properties.leg_type === "Rhumbline" ? "RL" : "GC";
                ppRoute.push(point);
            }
        }

        return ppRoute;
    }

    private _filterProperties(filter: string[], properties: { [index: string]: any }): { [index: string]: any } {
        const result: { [index: string]: any } = {};

        for (const propertyName in properties) {
            if (filter.includes(propertyName)) {
                result[propertyName] = properties[propertyName];
            }
        }

        return result;
    }

    // show popup reminder if an there is an executing voyage and the current plan is not approved
    private async _currentPlanOfExecutingVoyageApprovalReminder() {
        if (this._missingApprovedPlanAlreadyPrompted) {
            return
        }

        await this._loadPlansPromise;
        await this._updateExecutingPlanPromise;

        const scheduleEntries = await VoyageSchedulesModel!.getScheduleFor(VoyageImoModel!.imo!);

        if (!scheduleEntries) {
            return
        }

        const activeEntry = scheduleEntries.find(entry => entry.active);
        if (activeEntry && !activeEntry.inPort)
        {
            const currentPlan = await this.getCurrentPlan(activeEntry.id);
            const isBasicPlanAvailable = isVesselOnCloud && activeEntry.isBasicPlanAvailable;
            if (!isBasicPlanAvailable && (!currentPlan || (currentPlan && currentPlan.status !== "approved")))
            {
                const popupText = `Scheduled ETD is exceeded; and application has not detected an approved plan being sent.\nPlease create passage plan; approve it and have it sent to the Voyage Execution center.`;
                this.approvalReminder(popupText);
            }
        }
    }
    async isAlignedRoute(route: VoyageRoute, bprID: string): Promise<boolean> {
        return VesselPlansService.isRouteAlignedWithStarRoute(route, bprID);
    }

    async getRouteAdoption(route: VoyageRoute, bprID?: string, portPair?: RouteAdoptionPortPair, bprRoute?: VoyageRoute): Promise<RouteAdoptionResult> {
        return VesselPlansService.getRouteAdoption(route, bprID, portPair, bprRoute);
    }

    _appovePlanClf() {
        this.dispatchEvent(new CustomEvent("approve-plan-from-clf"));
    }

    _cancelPlanClf(): void {
        this.dispatchEvent(new CustomEvent("cancel-plan-from-clf"));
    }

    _savePassagePlan(): void {
        this.dispatchEvent(new CustomEvent("save-passage-plan"));
    }

    _localPassageUpdated(): void {
        this.dispatchEvent(new CustomEvent("local-passage-plan-updated"));
    }

    async getPlanSizeInMB(plan: VoyagePlan): Promise<number> {
        try {
            // Convert the plan object to JSON string to get its size
            const planJson = JSON.stringify(plan);
            
            // Calculate size in bytes using TextEncoder for accurate byte count
            const encoder = new TextEncoder();
            const planBytes = encoder.encode(planJson);
            
            // Convert bytes to MB (1 MB = 1,048,576 bytes)
            const sizeInMB = planBytes.length / (1024 * 1024);
            
            return sizeInMB;
        } catch (error) {
            console.error('Error calculating plan size:', error);
            return 0;
        }
    }

    // fetching plans and storing them in IndexedDB & deleting redundant plans
    private async savePlansToIndexedDb(plans: PlanWithId[], scheduleEntries: ScheduleEntry[]) {
        try {
            const scheduleIds = new Set(scheduleEntries.map((s) => s.id))

            const [
                existingSchedules,
                existingPlans,
                existingDraftSchedules,
                existingDraftPlans,
            ] = await Promise.all([
                IndexedDBService.schedules.getAll(),
                IndexedDBService.plans.getAll(),
                IndexedDraftsDBService.schedules.getAll(),
                IndexedDraftsDBService.plans.getAll(),
            ])

            if (plans?.length) {
                await IndexedDBService.plans.addAll(plans);
                // await Promise.all(
                //     plans.map((plan) =>
                //         Promise.all([
                //             IndexedDBService.plans.add(plan),
                //             IndexedDraftsDBService.plans.add(plan),
                //         ]),
                //     ),
                // )
            }

        await Promise.all([
                ...scheduleEntries.map((entry) =>
                    IndexedDBService.schedules.add(entry),
                ),
                ...scheduleEntries.map((entry) =>
                    IndexedDraftsDBService.schedules.add(entry),
                ),
            ])
        
        // delete schedules in the indexed-db that are not in the updated getSchedule call from BE 
            await this.deleteOutdatedSchedules(
                existingSchedules,
                existingPlans,
                scheduleIds,
                IndexedDBService,
            )
        // drafts to be removed
            await this.deleteOutdatedSchedules(
                existingDraftSchedules,
                existingDraftPlans,
                scheduleIds,
                IndexedDraftsDBService,
            )
        } catch (error) {
            console.error('Error adding schedules in DB', error)
        }
    }

    
    private async deleteOutdatedSchedules(
        existingSchedules: any[],
        existingPlans: any[],
        scheduleIds: Set<string>,
        service: any,
    ) {
        const deletions = []

        for (const existingEntry of existingSchedules) {
            if (!scheduleIds.has(existingEntry.id)) {
                deletions.push(service.schedules.delete(existingEntry.id))

                //delete plans associated with the schedule in the indexed-db
                deletions.push(
                    ...existingPlans
                        .filter((plan) => plan.scheduleId === existingEntry.id)
                        .map((plan) => service.plans.delete(plan.id)),
                )
            }
        }

        await Promise.all(deletions)
    }
}
